import React, { createContext, useContext, useState, useCallback, useMemo } from "react"
import type { DashboardFilter } from "@/pages/CustomDashboardPage"
import type { RelativeDateValue } from "@/data/types/views.types"

interface CustomDashboardContextType {
  // Dashboard data
  dashboardId: string | null
  dashboardName: string
  dashboardDescription: string
  dashboardFilters: DashboardFilter[]
  isRecommended: boolean
  relativeDate: RelativeDateValue | null

  // Original values for change detection
  originalName: string
  originalDescription: string
  originalFilters: DashboardFilter[]
  originalRelativeDate: RelativeDateValue | null

  // State setters
  setDashboardId: (id: string | null) => void
  setDashboardName: (name: string) => void
  setDashboardDescription: (description: string) => void
  setDashboardFilters: (filters: DashboardFilter[]) => void
  setIsRecommended: (recommended: boolean) => void
  setRelativeDate: (date: RelativeDateValue | null) => void

  // Initialize with original values
  initializeDashboard: (id: string, name: string, description: string, filters: DashboardFilter[], isRecommended?: boolean, relativeDate?: RelativeDateValue | null) => void

  // Reset after save
  markAsSaved: () => void

  // Clear on navigation away
  clearDashboard: () => void

  // Check if there are changes
  hasChanges: boolean

  // Modal state
  isEditModalOpen: boolean
  setIsEditModalOpen: (open: boolean) => void
  isSaveModalOpen: boolean
  setIsSaveModalOpen: (open: boolean) => void

  // Saving state
  isSaving: boolean
  setIsSaving: (saving: boolean) => void

  // Save handler (set by the page component)
  onSave: (() => Promise<void>) | null
  setOnSave: (handler: (() => Promise<void>) | null) => void
}

const CustomDashboardContext = createContext<CustomDashboardContextType | undefined>(undefined)

export function CustomDashboardProvider({ children }: { children: React.ReactNode }) {
  const [dashboardId, setDashboardId] = useState<string | null>(null)
  const [dashboardName, setDashboardName] = useState("")
  const [dashboardDescription, setDashboardDescription] = useState("")
  const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[]>([])
  const [isRecommended, setIsRecommended] = useState(false)
  const [relativeDate, setRelativeDate] = useState<RelativeDateValue | null>(null)

  const [originalName, setOriginalName] = useState("")
  const [originalDescription, setOriginalDescription] = useState("")
  const [originalFilters, setOriginalFilters] = useState<DashboardFilter[]>([])
  const [originalRelativeDate, setOriginalRelativeDate] = useState<RelativeDateValue | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [onSave, setOnSave] = useState<(() => Promise<void>) | null>(null)

  const initializeDashboard = useCallback((id: string, name: string, description: string, filters: DashboardFilter[], recommended: boolean = false, date: RelativeDateValue | null = null) => {
    setDashboardId(id)
    setDashboardName(name)
    setDashboardDescription(description)
    setDashboardFilters(filters)
    setIsRecommended(recommended)
    setRelativeDate(date)
    setOriginalName(name)
    setOriginalDescription(description)
    setOriginalFilters(filters)
    setOriginalRelativeDate(date)
  }, [])

  const markAsSaved = useCallback(() => {
    setOriginalName(dashboardName)
    setOriginalDescription(dashboardDescription)
    setOriginalFilters(dashboardFilters)
    setOriginalRelativeDate(relativeDate)
  }, [dashboardName, dashboardDescription, dashboardFilters, relativeDate])

  const clearDashboard = useCallback(() => {
    setDashboardId(null)
    setDashboardName("")
    setDashboardDescription("")
    setDashboardFilters([])
    setIsRecommended(false)
    setRelativeDate(null)
    setOriginalName("")
    setOriginalDescription("")
    setOriginalFilters([])
    setOriginalRelativeDate(null)
    setOnSave(null)
  }, [])

  const hasChanges = useMemo(() => {
    if (!dashboardId) return false

    const nameChanged = dashboardName !== originalName
    const descriptionChanged = dashboardDescription !== originalDescription
    const filtersChanged = JSON.stringify(dashboardFilters) !== JSON.stringify(originalFilters)
    const relativeDateChanged = relativeDate !== originalRelativeDate

    return nameChanged || descriptionChanged || filtersChanged || relativeDateChanged
  }, [dashboardId, dashboardName, dashboardDescription, dashboardFilters, relativeDate, originalName, originalDescription, originalFilters, originalRelativeDate])

  const value: CustomDashboardContextType = {
    dashboardId,
    dashboardName,
    dashboardDescription,
    dashboardFilters,
    isRecommended,
    relativeDate,
    originalName,
    originalDescription,
    originalFilters,
    originalRelativeDate,
    setDashboardId,
    setDashboardName,
    setDashboardDescription,
    setDashboardFilters,
    setIsRecommended,
    setRelativeDate,
    initializeDashboard,
    markAsSaved,
    clearDashboard,
    hasChanges,
    isEditModalOpen,
    setIsEditModalOpen,
    isSaveModalOpen,
    setIsSaveModalOpen,
    isSaving,
    setIsSaving,
    onSave,
    setOnSave,
  }

  return (
    <CustomDashboardContext.Provider value={value}>
      {children}
    </CustomDashboardContext.Provider>
  )
}

export function useCustomDashboard() {
  const context = useContext(CustomDashboardContext)
  if (context === undefined) {
    throw new Error("useCustomDashboard must be used within a CustomDashboardProvider")
  }
  return context
}

